/**
 * The console's two unauthenticated screens share one frame — design
 * "Led AI - Console Sign In", 1a (sign in) and 2a (sign up).
 *
 * ## What is ours and what is Clerk's
 *
 * The LEFT half is this file's: the ink panel, the brand, the promise, and —
 * depending on which door you came through — the gold waveform readout or the
 * three things the product does for you. The RIGHT half is Clerk's own
 * `<SignIn>` / `<SignUp>`, restyled through its `appearance` API into the
 * artboard's form.
 *
 * That division is deliberate. Everything behind the form — OAuth round
 * trips, email codes, passwords, passkeys, second factors, rate limits, the
 * reset flow, every error string — is auth, and re-implementing it against
 * `useSignIn` to gain pixel control over a button radius would put the one
 * screen nobody can work around on code we maintain ourselves. The
 * artboard's anatomy is Clerk's anatomy (title, social row, "or", email,
 * continue, alternative method, footer action), so the theming lands close
 * without that trade.
 *
 * What Clerk still owns, and what the artboards therefore cannot dictate: the
 * ORDER of the social buttons, whether a passkey option appears at all (it
 * follows the instance's enabled strategies), and the wording of anything not
 * overridden in `AUTH_LOCALIZATION` — including the email field's label,
 * which is one string for every screen, so 2a's "Work email" stays Clerk's
 * "Email address".
 *
 * ## The frame takes the form as a slot
 *
 * `AuthFrame` is a plain component that knows nothing about Clerk, so it
 * renders — and is tested — in a DOM with no `<ClerkProvider>` above it. Same
 * reason `AuthIdentity` is a slot the shell fills: the console has a real
 * ungated mode, and a layout that imported Clerk could not be exercised
 * without one. `SignInScreen` and `SignUpScreen` are the pieces assembled.
 */

import type { ReactNode } from "react";
import { Lockup } from "../components/Lockup";
import { useMediaQuery } from "../shell/useMediaQuery";
import { PROCORE_MARK_URL } from "../assets/procoreMark";
import { C, FONT_MONO, FONT_TEXT } from "../tokens";

/** Which door the visitor came through. The form differs; the panel differs
 *  only in what it offers as proof. */
export type AuthScreen = "sign-in" | "sign-up";

/** Below this the brand panel is dropped and the form takes the page. The
 *  panel is half a 1180px artboard; squeezed under ~900px its 44px headline
 *  breaks into five lines and the form loses the room it needs. The shell's
 *  own breakpoint is 820px and is not this one — that one swaps a sidebar for
 *  a drawer, this one drops an ornament. */
const PANEL_BREAKPOINT = "(max-width: 900px)";

const mono = (size: number, tracking: number): React.CSSProperties => ({
  fontFamily: FONT_MONO,
  fontSize: size,
  fontWeight: 700,
  letterSpacing: tracking,
  textTransform: "uppercase",
});

/**
 * The gold waveform in the readout pill — the app's capture bars, at rest on
 * a page where nothing is capturing.
 *
 * Twelve bars with heights and phases derived from the index rather than from
 * `Math.random`, so the panel looks the same on every load and a screenshot
 * diff of this screen means something. The animation itself is a class, not
 * an inline `animation` shorthand: `main.tsx` declares every animated rule
 * inside the `prefers-reduced-motion: no-preference` query, so with motion
 * off the bars are simply a static gold row.
 */
function Waveform() {
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 3, height: 22 }} aria-hidden>
      {Array.from({ length: 12 }, (_, i) => (
        <span
          key={i}
          className="signin-bar"
          style={{
            width: 3,
            height: 6 + ((i * 37) % 16),
            borderRadius: 1.5,
            background: C.gold,
            display: "block",
            animationDelay: `${(((i * 7) % 6) * 0.1).toFixed(1)}s`,
            animationDuration: `${(0.5 + ((i * 13) % 5) * 0.1).toFixed(1)}s`,
          }}
        />
      ))}
    </span>
  );
}

/**
 * A partner's mark on the ink panel.
 *
 * The artboard uses knocked-back horizontal WORDMARKS, inverted to white.
 * This repo has neither company's wordmark — what it has, and what the iOS
 * app's integrations card and New Project flow already show, are the square
 * marks (`providerMarks.tsx`). Those are drawn in each brand's own ink on
 * transparent, so on this panel they get the near-white tile the phone gives
 * them rather than an `invert()` filter, which would turn Procore's orange
 * hex cyan.
 */
function PartnerTile({ children, label }: { children: ReactNode; label: string }) {
  return (
    <span
      role="img"
      aria-label={label}
      style={{
        width: 28,
        height: 28,
        borderRadius: 8,
        background: C.signInTile,
        display: "grid",
        placeItems: "center",
        flexShrink: 0,
      }}
    >
      {children}
    </span>
  );
}

/**
 * Autodesk's mark — the folded-ribbon A, inline, the same path
 * `providerMarks.tsx` draws. Inline rather than imported across the app
 * boundary: `webapp` bundles its own client and `bun test` has no PNG/asset
 * loader (see `assets/lockup.ts`), and this is one path.
 */
function AutodeskMark() {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" aria-hidden>
      <path
        d="m.129 20.202 14.7-9.136h7.625c.235 0 .445.188.445.445 0 .21-.092.305-.21.375l-7.222 4.323c-.47.283-.633.845-.633 1.265l-.008 2.725H24V4.362a.561.561 0 0 0-.585-.562h-8.752L0 12.893V20.2h.129z"
        fill={C.ink}
      />
    </svg>
  );
}

/**
 * What 2a puts where 1a has the live readout.
 *
 * A returning user is told the product is RUNNING; someone creating an
 * account has nothing to return to, so the panel tells them what they are
 * about to get instead. Verbatim from the artboard's own `perks`.
 */
const PERKS = [
  "Walks transcribed and structured in under a minute",
  "Photos, locations and assignees filled in for you",
  "Two-way sync with Procore and Autodesk",
];

/** A perk, with the gold tick the artboard draws in a 22px disc. */
function Perk({ label }: { label: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <span
        style={{
          width: 22,
          height: 22,
          borderRadius: 11,
          background: C.signInTick,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: "none",
        }}
      >
        <svg width={12} height={12} viewBox="0 0 24 24" aria-hidden>
          <path
            d="M20 6 9 17l-5-5"
            fill="none"
            stroke={C.gold}
            strokeWidth={3}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <span style={{ fontSize: 14.5, fontWeight: 600, color: C.signInOnStrong }}>{label}</span>
    </div>
  );
}

/** The brand half. Ink ground, white mark, the promise, the proof, the
 *  partners, and a gold bloom pushed off the bottom-right corner. */
function BrandPanel({ screen }: { screen: AuthScreen }) {
  return (
    <div
      data-testid="auth-brand"
      style={{
        position: "relative",
        overflow: "hidden",
        background: C.ink,
        color: C.signInOn,
        padding: "44px 48px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12, color: C.signInOn }}>
        <Lockup height={34} variant="mark" tone={C.signInOn} />
        <span style={{ ...mono(10.5, 1.8), color: C.signInOnMeta }}>Led AI Console</span>
      </div>

      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: 28,
          padding: "40px 0",
        }}
      >
        <h1
          style={{
            fontSize: 44,
            fontWeight: 800,
            letterSpacing: "-1.8px",
            lineHeight: 1.02,
            textWrap: "balance",
          }}
        >
          Instant documentation.
          <br />
          No typing.
        </h1>
        <p
          style={{
            fontSize: 16,
            lineHeight: 1.55,
            color: C.signInOnSoft,
            maxWidth: 380,
            textWrap: "pretty",
          }}
        >
          Every walk your team records, reviewed and synced to Procore and Autodesk — all in one
          console.
        </p>
        {/*
          * WHAT THE PANEL OFFERS AS PROOF, and why it differs by door.
          *
          * 1a shows a live-walk readout — but it is COPY, not a reading.
          * Nobody is signed in on this page, so there is no workspace to
          * count and nothing this panel could honestly report. The artboard's
          * line ships as written, as a picture of the product rather than as
          * live state, and must never be wired to a real count: an
          * unauthenticated visitor is exactly who must not learn how many
          * walks are running.
          *
          * 2a shows three perks instead. Someone creating an account has
          * nothing to return to, so the panel says what they are about to get.
          */}
        {screen === "sign-up" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12, width: "fit-content" }}>
            {PERKS.map((perk) => (
              <Perk key={perk} label={perk} />
            ))}
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              padding: "14px 16px",
              borderRadius: 14,
              background: C.signInChip,
              border: `1px solid ${C.signInChipEdge}`,
              width: "fit-content",
            }}
          >
            <Waveform />
            <span style={{ ...mono(10, 1.4), color: C.signInOnMeta }}>
              3 walks live · 101 Broadway
            </span>
          </div>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{ ...mono(9.5, 1.3), color: C.signInOnSoft }}>Syncs with</span>
        <PartnerTile label="Procore">
          <img src={PROCORE_MARK_URL} alt="" width={22} height={22} style={{ display: "block" }} />
        </PartnerTile>
        <PartnerTile label="Autodesk Construction Cloud">
          <AutodeskMark />
        </PartnerTile>
      </div>

      {/* The bloom. Pushed past two edges so it reads as light entering the
          panel rather than as a circle drawn on it. */}
      <span
        aria-hidden
        style={{
          position: "absolute",
          right: -120,
          bottom: -140,
          width: 420,
          height: 420,
          borderRadius: 210,
          background: `radial-gradient(circle, ${C.signInGlow}, ${C.signInGlowFade} 70%)`,
          display: "block",
          pointerEvents: "none",
        }}
      />
    </div>
  );
}

/**
 * The page: brand panel beside a form.
 *
 * `form` is a slot — see the file header for why this component must not know
 * about Clerk.
 *
 * `data-auth-frame` is what `main.tsx` scopes its Clerk overrides to. It has
 * to be an attribute on the OUTERMOST element and it has to name the screen:
 * the rules that hide Clerk's own footer apply to sign-up alone, where the
 * terms line and the "Already have an account?" row are ours.
 */
export function AuthFrame({ screen, form }: { screen: AuthScreen; form: ReactNode }) {
  const narrow = useMediaQuery(PANEL_BREAKPOINT);
  return (
    <div
      data-auth-frame={screen}
      data-testid={screen === "sign-up" ? "signup" : "signin"}
      style={{
        minHeight: "100%",
        display: "grid",
        gridTemplateColumns: narrow ? "1fr" : "1fr 1fr",
        background: C.canvas,
        color: C.ink,
        fontFamily: FONT_TEXT,
      }}
    >
      {narrow ? null : <BrandPanel screen={screen} />}
      {/*
        * NOT a scroll container.
        *
        * `overflowY: "auto"` here cut the "Last used" badge in half. CSS
        * resolves the other axis of a scrolling box to `auto` as well, so a
        * column that scrolls vertically also clips horizontally — and Clerk
        * hangs that badge off the top-RIGHT corner of the last-used provider,
        * past the button's own edge. The page scrolls instead: `minHeight:
        * 100%` lets this row grow and the document take it, which is what a
        * short viewport needs anyway and clips nothing.
        */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: narrow ? "48px 24px" : 48,
          minWidth: 0,
        }}
      >
        {/*
          * `minWidth: 0` is what makes `maxWidth: 100%` mean anything here.
          * A flex item defaults to `min-width: auto`, so it refuses to shrink
          * below its content's min-content width — the form stayed 380px wide
          * inside a 372px box and the PAGE scrolled sideways at 420px.
          */}
        <div data-testid="auth-form" style={{ width: 380, maxWidth: "100%", minWidth: 0 }}>
          {narrow ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                color: C.ink,
                marginBottom: 32,
              }}
            >
              <Lockup height={30} variant="mark" />
              <span style={{ ...mono(10.5, 1.8), color: C.sub }}>Led AI Console</span>
            </div>
          ) : null}
          {form}
        </div>
      </div>
    </div>
  );
}

/** The eyebrow the artboard sets the "or" divider, the field label and
 *  2a's step marker in. */
export const EYEBROW: React.CSSProperties = {
  ...mono(10, 1.6),
  color: C.sub2,
};

/**
 * The artboard's form, expressed in Clerk's element keys.
 *
 * Clerk's card chrome is removed entirely (`card`/`cardBox`: transparent, no
 * border, no shadow, no padding) because the paper half of 1a IS the page —
 * the form sits on the console's canvas, not on a second surface. Everything
 * below that is the artboard's measurements: 48px controls at radius 12, a
 * 52px ink pill at radius 999, hairlines at `C.line`.
 *
 * Pseudo-states are written as nested `&:hover` / `&:focus` keys, which
 * Clerk's appearance API compiles to real CSS — inline style cannot express
 * them, and a control with no hover on the console's one unauthenticated
 * screen reads as a picture of a button.
 */
export const AUTH_APPEARANCE = {
  layout: {
    socialButtonsVariant: "blockButton" as const,
    // The "Development mode" badge Clerk prints under a `pk_test_` key. It is
    // Clerk's chrome on our sign-in page, it says nothing to the person
    // signing in, and on staging it reads as though the console itself were a
    // prototype. Which key is in use is `/__env`'s answer, not a badge's.
    unsafe_disableDevelopmentModeWarnings: true,
    // Clerk's own logo above the title would be a second brand mark on a page
    // that already has ours in the panel beside it.
    logoPlacement: "none" as const,
  },
  variables: {
    colorPrimary: C.ink,
    colorText: C.ink,
    colorTextSecondary: C.sub,
    colorBackground: C.canvas,
    colorInputBackground: C.surface,
    colorInputText: C.ink,
    colorDanger: C.red,
    colorSuccess: C.green,
    colorWarning: C.amber,
    fontFamily: FONT_TEXT,
    fontSize: "15px",
    borderRadius: "12px",
  },
  elements: {
    rootBox: { width: "100%" },
    cardBox: {
      width: "100%",
      boxShadow: "none",
      border: "none",
      borderRadius: 0,
      background: "transparent",
    },
    card: {
      width: "100%",
      background: "transparent",
      boxShadow: "none",
      border: "none",
      padding: 0,
      gap: 0,
      /*
       * Clerk ships the card at `margin: -1px -1px 0` — it compensates the
       * 1px border the card normally draws, pulling the box back so the
       * border sits ON the boundary rather than outside it.
       *
       * We removed that border (the form sits on the page here, not on a
       * second surface), so the compensation had nothing left to compensate:
       * every control was laid out ONE PIXEL past the column's left edge and
       * one short of its right. Harmless-looking, until the column clipped —
       * then the left edge of every button was shaved off, which is exactly
       * how it was reported. Measured in Chrome: card at 889, column at 890.
       */
      margin: 0,
    },
    header: { display: "block", textAlign: "left", gap: 0 },
    headerTitle: {
      fontSize: 30,
      fontWeight: 800,
      letterSpacing: "-1px",
      lineHeight: 1.1,
      textAlign: "left",
      color: C.ink,
    },
    headerSubtitle: {
      fontSize: 14.5,
      lineHeight: 1.5,
      color: C.sub,
      marginTop: 8,
      textAlign: "left",
    },
    main: { gap: 0 },
    // Clerk's own wrapper around the grid. When the client has a last-used
    // provider that provider is lifted OUT of the grid into its own
    // full-width row here, so this is where the rhythm between the two rows
    // is set — and where a clipping box would cut the badge.
    socialButtonsRoot: { marginTop: 28, gap: 10, overflow: "visible" },
    socialButtons: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 10,
      marginTop: 0,
      overflow: "visible",
      // The artboard draws TWO providers, side by side. The instance has
      // three (Apple, Google, Microsoft) and may have more later, so an odd
      // last button spans the row rather than sitting alone in the left
      // column with a hole beside it.
      "& > *:last-child:nth-child(odd)": { gridColumn: "span 2" },
    },
    socialButtonsBlockButton: {
      height: 48,
      margin: 0,
      borderRadius: 12,
      background: C.surface,
      border: `1px solid ${C.line}`,
      boxShadow: "none",
      color: C.ink,
      cursor: "pointer",
      position: "relative",
      transition: "background-color 120ms ease, border-color 120ms ease",
      // A hover you can SEE. The first pass moved the ground from C.surface to
      // C.inset — four units — and left the border to carry the whole state,
      // which on a 1px edge over paper reads as nothing happening at all;
      // the screen looked unresponsive to the pointer. `sunk` is a real step
      // off `surface` and is the console's own pressed-surface tone.
      "&:hover": { background: C.sunk, borderColor: C.ink },
      "&:focus-visible": { background: C.sunk, borderColor: C.ink, outline: "none" },
      "&:active": { background: C.faint, transform: "scale(0.985)" },
    },
    socialButtonsBlockButtonText: {
      fontSize: 14.5,
      fontWeight: 700,
      letterSpacing: "-0.2px",
      color: C.ink,
    },
    /*
     * "Last used", on the provider this browser signed in with before.
     *
     * Ink, not paper: it sits ON the button's top edge, and Clerk's default
     * is a pale chip with a hairline — over a white 48px control that reads
     * as a second, broken border rather than as a label. Ink makes it a
     * marker, and matches the CTA below it.
     *
     * `right: 12` keeps it INSIDE the button. Clerk hangs it past the corner
     * by default, which is what a clipping ancestor cut in half; the column
     * no longer clips (see `SignInFrame`), and the badge no longer needs it
     * not to.
     *
     * `main.tsx` carries the same values as a scoped rule, because an
     * appearance key can lose to Clerk's own CSS — the field's `height` did.
     */
    lastAuthenticationStrategyBadge: {
      ...mono(9, 1.2),
      position: "absolute",
      top: 0,
      right: 12,
      transform: "translateY(-50%)",
      background: C.ink,
      color: C.signInOn,
      border: "none",
      borderRadius: 999,
      padding: "3px 8px",
      whiteSpace: "nowrap",
      maxWidth: "none",
      overflow: "visible",
      textOverflow: "clip",
    },
    dividerRow: { margin: "22px 0", gap: 12 },
    dividerLine: { background: C.line },
    dividerText: EYEBROW,
    form: { gap: 14 },
    formFieldRow: { gap: 8 },
    formField: { gap: 8 },
    formFieldLabel: { ...EYEBROW, color: C.ink },
    formFieldInput: {
      // `minHeight`, not `height` alone: Clerk's own rule for the input lands
      // after the appearance one and computes it back to 36px (measured in
      // Chrome — `height: 48` alone renders a 36px field). `min-height` beats
      // `height` in layout, so the 48px control the design draws survives
      // whichever rule wins the cascade.
      height: 48,
      minHeight: 48,
      padding: "0 16px",
      fontSize: 15,
      borderRadius: 12,
      background: C.surface,
      border: `1px solid ${C.line}`,
      boxShadow: "none",
      color: C.ink,
      "&:focus": { borderColor: C.ink, boxShadow: "none", outline: "none" },
    },
    formFieldAction: { fontSize: 13, fontWeight: 700, color: C.goldInk },
    /* "Optional", beside a name field on sign-up. Clerk sets it at the
     *  label's own size, which puts two competing labels on one row; the
     *  design's field labels are the mono eyebrow and this is a footnote to
     *  one of them. */
    formFieldHintText: { fontSize: 11.5, fontWeight: 400, color: C.sub2, letterSpacing: 0 },
    formButtonPrimary: {
      height: 52,
      borderRadius: 999,
      background: C.ink,
      color: C.signInOn,
      fontSize: 15.5,
      fontWeight: 700,
      letterSpacing: "-0.2px",
      textTransform: "none",
      boxShadow: "none",
      "&:hover": { background: C.ink, opacity: 0.9 },
      "&:active": { transform: "scale(0.98)" },
      "&:after": { color: C.gold },
    },
    // The chevron Clerk draws inside the primary button — gold, the one
    // brand-coloured thing on the paper half, exactly as 1a has it.
    buttonArrowIcon: { color: C.gold },
    otpCodeFieldInput: {
      height: 48,
      minHeight: 48,
      borderRadius: 12,
      background: C.surface,
      border: `1px solid ${C.line}`,
      color: C.ink,
    },
    alternativeMethods: { marginTop: 18, gap: 10 },
    alternativeMethodsBlockButton: {
      height: 48,
      borderRadius: 12,
      background: C.surface,
      border: `1px solid ${C.line}`,
      fontSize: 13.5,
      fontWeight: 700,
      color: C.ink,
      boxShadow: "none",
      cursor: "pointer",
      transition: "background-color 120ms ease, border-color 120ms ease",
      "&:hover": { background: C.sunk, borderColor: C.ink },
      "&:active": { background: C.faint },
    },
    /* "Use passkey instead" renders as a LINK rather than a block button on
     *  the start step. Without this it is the one control on the page that
     *  answers the pointer with nothing. */
    alternativeMethodsBlockButtonText: { cursor: "pointer" },
    backLink: {
      fontSize: 13.5,
      fontWeight: 700,
      color: C.goldInk,
      cursor: "pointer",
      "&:hover": { color: C.ink },
    },
    /*
     * "Use passkey instead" (design 1a, the row under Continue).
     *
     * Clerk renders it as a CARD ACTION — `footerAction` with the
     * `usePasskey` id, which is why it arrived wearing `footerActionLink`'s
     * gold and sitting hard against the CTA, left-aligned. The design puts it
     * centred, 18px down, in quiet ink: an alternative route, not a second
     * primary action and not a link into somewhere else.
     *
     * Clerk's own device glyph is not ours to place — the artboard sets a
     * 14px one before the label, and inventing a substitute would put a
     * glyph on this page that appears nowhere else in the console.
     */
    footerAction__usePasskey: {
      marginTop: 18,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
    },
    footer: {
      background: "transparent",
      borderTop: `1px solid ${C.hair}`,
      marginTop: 30,
      paddingTop: 18,
      gap: 0,
    },
    footerAction: { margin: 0 },
    footerActionText: { fontSize: 13.5, color: C.sub },
    footerActionLink: {
      fontSize: 13.5,
      fontWeight: 700,
      color: C.goldInk,
      "&:hover": { color: C.ink },
    },
    // Clerk's "Help / Privacy / Terms" row. The console's own footer links
    // are not these, and an unset page here renders a dead link.
    footerPages: { display: "none" },
    // Clerk's logo above the title, belt-and-braces with `logoPlacement`.
    // The page already carries one brand mark, in the panel beside this.
    logoBox: { display: "none" },
    identityPreviewEditButton: { color: C.goldInk },
  },
};

/**
 * The strings the artboards rewrite.
 *
 * Clerk's defaults are close but not the artboard's: the subtitle is
 * "Welcome back! Please sign in to continue", and a social button reads
 * "Continue with Google", which is too long for the two-up row 1a puts them
 * in. `{{provider|titleize}}` is Clerk's own template variable, so a provider
 * enabled later still names itself correctly.
 */
export const AUTH_LOCALIZATION = {
  socialButtonsBlockButton: "{{provider|titleize}}",
  // The artboard's field shows the SHAPE of the answer. Clerk's default
  // ("Enter your email address") only repeats the label above it.
  formFieldInputPlaceholder__emailAddress: "name@company.com",
  formFieldInputPlaceholder__emailAddress_username: "name@company.com",
  signIn: {
    start: {
      title: "Sign in",
      subtitle: "Welcome back. Use your work account to continue.",
      actionText: "Don't have an account?",
      actionLink: "Sign up",
    },
  },
  signUp: {
    start: {
      title: "Create your account",
      subtitle: "Use your work account so your team can find you.",
      actionText: "Already have an account?",
      actionLink: "Sign in",
    },
  },
};

